import { useCallback } from "react";
import { useGeneralContext } from "../context/AppGeneralContext";
import { COOKIE_TYPES } from "../enums/cookies";
import { checkHasCompletedProfile } from "../helpers/profileCompletion";
import { gdprCookieHasValue } from "../utils/cookieUtils";
import { scrubUrl } from "../utils/scrubUrl";

export const useLogger = () => {
    const { userInfo, teacherInfo, hasVocabTraining, hasTest, creatingMode } = useGeneralContext();

    // Consent changes always arrive via a full page reload (cookie.service.ts), so a
    // fresh read per render is enough — no cookie subscription needed.
    const hasAnalyticsConsent = gdprCookieHasValue(COOKIE_TYPES.ANALYTICS);

    const logEvent = useCallback(
        (eventName: string, props: { [key: string]: any } = {}) => {
            if (!hasAnalyticsConsent) {
                if (process.env.NODE_ENV !== "production") {
                    console.log("[logEvent] dropped (no analytics consent)", eventName);
                }
                return;
            }

            const userRole = userInfo.isAnonymousUser ? "anonymous" : userInfo.visitorOrUserRole ?? "other";

            const eventProperties = {
                app_source: "teacher_tools",
                page_path: scrubUrl(window.location.pathname),
                user_role: userRole,
                ...(window.location.pathname.includes("/create") ? { creating_mode: creatingMode } : {}),
                onboarding_profile_completed: checkHasCompletedProfile(teacherInfo),
                onboarding_test_created: hasTest,
                onboarding_training_created: hasVocabTraining === true,
                ...props,
            };

            if (process.env.NODE_ENV !== "production") {
                console.log("[logEvent]", eventName, eventProperties);
            }

            // GTM boots lazily (only after consent), so on a hard page load the dataLayer may
            // not exist yet when the first events fire. Create the queue ourselves — GTM picks
            // up everything pushed before its script loads.
            window.dataLayer = window.dataLayer || [];

            // GTM deep-merges every push into one persistent data model, so the nested
            // eventProperties keys from earlier events linger and ride along on later ones.
            // Reset the object first (Google's recommended pattern) so each event sent to
            // Amplitude carries only its own properties.
            window.dataLayer.push({ eventProperties: null });
            window.dataLayer.push({ event: "logEvent", eventName, eventProperties });
        },
        [
            hasAnalyticsConsent,
            userInfo.isAnonymousUser,
            userInfo.visitorOrUserRole,
            teacherInfo,
            hasTest,
            hasVocabTraining,
            creatingMode,
        ]
    );

    return { logEvent };
};
